// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip } from '@mui/material';
import {
  capitalizeString,
  classNames,
  formatDate,
  NUMERIC_DATE_FORMAT_OPTIONS,
  parseDate,
} from '@shared/ui-kit/utils';
import { isDefined, isNull, isNullOrUndefined } from '@shared/utils';
import { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type CellValue = string | number | Date | undefined | null;

export type CellDataFormat =
  | 'currency'
  | 'number'
  | 'decimal'
  | 'percentage'
  | 'date'
  | 'performance'
  | 'text'
  | 'capitalized';

export type FormatOptions<T extends CellValue> = T extends Date
  ? Intl.DateTimeFormatOptions
  : T extends number
  ? Intl.NumberFormatOptions
  : Record<string, unknown>;

export interface TableDataProps<T extends CellValue = CellValue> {
  value?: T;
  format: CellDataFormat;
  currency?: string;
  formatOptions?: FormatOptions<T>;
  safeValue?: string;
  emptyValue?: CellValue;
  validate?: ((value?: T) => boolean) | boolean;
  /**
   * When true, non-positive values render as favorable (green) and positive as
   * unfavorable (red).
   */
  lowerIsBetter?: boolean;
  container?: boolean;
  className?: string;
  oneLine?: boolean;
  showTooltip?: boolean;
  applyPercentageTransform?: boolean;
  decimalPlaces?: number;
}

export interface TableDataComponent {
  <T extends CellValue = CellValue>(
    props: TableDataProps<T>,
    context?: any,
  ): ReactElement<any, any> | null;
}

export const CellData: TableDataComponent = ({
  value,
  format,
  currency,
  formatOptions,
  safeValue = '',
  emptyValue,
  validate = false,
  lowerIsBetter = false,
  container = true,
  className,
  oneLine = false,
  showTooltip = false,
  applyPercentageTransform = true,
  decimalPlaces = 2,
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

  const formattedValue = useMemo(() => {
    if (
      isNullOrUndefined(value) ||
      (typeof value === 'number' && Number.isNaN(value)) ||
      value === emptyValue ||
      (value instanceof Date && Number.isNaN(value.getTime())) ||
      (typeof value === 'string' && format === 'date' && isNull(parseDate(value)))
    ) {
      return safeValue;
    }

    if (typeof value === 'string' && value === '' && format !== 'text') {
      return safeValue;
    }

    switch (format) {
      case 'text':
        return String(value);
      case 'capitalized':
        return capitalizeString(String(value));
      case 'performance': {
        const performanceValue = value as number;

        if (performanceValue === -1) return safeValue;

        const percentage = (performanceValue * 100).toFixed(decimalPlaces);

        return performanceValue >= 0 ? `+${percentage}%` : `${percentage}%`;
      }
      case 'date': {
        const dateOptions = {
          ...NUMERIC_DATE_FORMAT_OPTIONS,
          ...((formatOptions as Intl.DateTimeFormatOptions) ?? {}),
        };

        return (
          formatDate(
            value as Date | string,
            'standard',
            i18n.language,
            dateOptions,
          ) ?? safeValue
        );
      }
      case 'currency':
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency || 'USD',
          trailingZeroDisplay: 'stripIfInteger',
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          useGrouping: true,
          ...(formatOptions ?? {}),
        }).format(value as number);
      case 'number':
      case 'decimal':
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: format === 'decimal' ? 1 : 0,
          maximumFractionDigits: format === 'decimal' ? 2 : decimalPlaces,
          trailingZeroDisplay: 'stripIfInteger',
          useGrouping: true,
          ...(formatOptions ?? {}),
        }).format(value as number);
      case 'percentage': {
        const numericValue = value as number;

        if (!applyPercentageTransform) {
          return `${numericValue.toFixed(decimalPlaces)}%`;
        }

        return new Intl.NumberFormat(locale, {
          style: 'percent',
          minimumFractionDigits: validate ? decimalPlaces : 1,
          maximumFractionDigits: validate ? decimalPlaces : 1,
          useGrouping: true,
          ...(formatOptions ?? {}),
        }).format(numericValue);
      }
    }
  }, [
    applyPercentageTransform,
    currency,
    decimalPlaces,
    emptyValue,
    format,
    formatOptions,
    i18n.language,
    locale,
    safeValue,
    validate,
    value,
  ]);

  const isInvalid = useMemo(() => {
    switch (typeof validate) {
      case 'function':
        return validate(value);
      case 'boolean': {
        if (
          validate &&
          typeof value === 'number' &&
          ['currency', 'number', 'decimal', 'percentage', 'performance'].includes(
            format,
          )
        ) {
          return value <= 0;
        }

        return undefined;
      }
      default:
        return undefined;
    }
  }, [format, validate, value]);

  const displayClassName = classNames(
    {
      '!money-display': [
        'currency',
        'number',
        'decimal',
        'percentage',
        'performance',
      ].includes(format),
    },
    { 'line-clamp-1': oneLine },
    format === 'performance' &&
      typeof value === 'number' &&
      value !== -1 &&
      isDefined(value) &&
      (value >= 0 ? '!text-positive' : '!text-negative'),
    className,
  );

  const innerContent = container ? (
    <span
      className={classNames(
        lowerIsBetter
          ? 'aria-[invalid=false]:text-negative aria-[invalid=true]:text-positive'
          : 'aria-[invalid=false]:text-positive aria-[invalid=true]:text-negative',
      )}
      aria-invalid={isInvalid}
    >
      {formattedValue}
    </span>
  ) : (
    formattedValue
  );

  const output = (
    <span
      className={classNames({
        '!money-display': ['currency', 'number', 'decimal', 'percentage'].includes(
          format,
        ),
      })}
    >
      <span className={displayClassName}>{innerContent}</span>
    </span>
  );

  if (showTooltip || oneLine) {
    return (
      <Tooltip title={String(value ?? '')} placement="top">
        <span className={displayClassName}>{formattedValue}</span>
      </Tooltip>
    );
  }

  return output;
};
